/**
 * KitchenCardPreview — Slide-out panel for previewing & downloading kitchen cards.
 * Also exports generateRecipeBook() for full recipe book generation.
 */

import React, { useState, useRef, useCallback } from 'react';
import { X, Download, Loader2, Printer } from 'lucide-react';
import type { Recipe, Allergen } from '../types';
import { formatKitchenCard } from '../services/RecipeEngineService';
import { apiFetch } from '../../../data/ApiModuleRepository';
import { getMediaUrl } from '../../../core/fileUpload';
import { cn } from '../../../ui/cn';
import { useToast } from '../../../ui/Toast';

interface KitchenCardPreviewProps {
  open: boolean;
  onClose: () => void;
  recipe: Recipe;
  itemName: string;
  itemImage?: string;
  profileId: string;
  brandName?: string;
  brandLogo?: string;
}

/* ═══════════════════════════════════════════════════════════════════════════
   RECIPE CARD — Professional landscape card for wall display
   ═══════════════════════════════════════════════════════════════════════════ */
function buildCardHtml(opts: {
  itemName: string;
  itemImage?: string;
  ingredients: { name: string; qty: string }[];
  steps: string[];
  allergens: Allergen[];
  yield: number;
  prepTime?: number;
  brandName?: string;
  brandLogo?: string;
  forPrint?: boolean;
  pageNum?: number;
}): string {
  const {
    itemName, itemImage, ingredients, steps, allergens,
    yield: yld, prepTime, brandName, brandLogo, forPrint, pageNum,
  } = opts;

  const brandLogoUrl = brandLogo ? getMediaUrl(brandLogo) : null;
  const itemImgUrl = itemImage ? getMediaUrl(itemImage) : null;

  const ingredientRows = ingredients.map((ing, idx) =>
    `<tr style="background:${idx % 2 === 0 ? '#ffffff' : '#f8fafc'};">
      <td style="padding:3px 10px;font-size:11.5px;color:#1e293b;border-bottom:1px solid #f1f5f9;">${ing.name}</td>
      <td style="padding:3px 10px;font-size:11.5px;text-align:right;font-weight:700;color:#0f172a;border-bottom:1px solid #f1f5f9;white-space:nowrap;">${ing.qty}</td>
    </tr>`
  ).join('');

  const stepsHtml = steps.map((s, i) =>
    `<div style="display:flex;gap:10px;margin-bottom:10px;align-items:flex-start;">
      <div style="width:22px;height:22px;min-width:22px;border-radius:50%;background:linear-gradient(135deg,#4f46e5,#7c3aed);color:white;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:10px;line-height:22px;flex-shrink:0;box-shadow:0 2px 4px rgba(79,70,229,0.3);">${i + 1}</div>
      <p style="flex:1;font-size:12px;line-height:1.6;color:#334155;margin:0;padding-top:1px;">${s}</p>
    </div>`
  ).join('');

  const allergenChips = allergens.length > 0
    ? `<div style="margin-top:16px;">
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
          <span style="font-size:12px;">⚠️</span>
          <span style="font-weight:800;font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#dc2626;">Allergens</span>
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:5px;">
          ${allergens.map(a => `<span style="padding:3px 10px;border-radius:6px;background:#fef2f2;border:1px solid #fecaca;color:#b91c1c;font-size:10px;font-weight:700;">${a}</span>`).join('')}
        </div>
      </div>` : '';

  const metaBadges = `<div style="display:flex;gap:8px;margin-top:16px;">
    <div style="display:flex;align-items:center;gap:5px;padding:5px 12px;background:#ecfdf5;border:1px solid #a7f3d0;border-radius:6px;">
      <span style="font-size:12px;">🍽️</span>
      <span style="font-size:11px;font-weight:700;color:#065f46;">${yld} portion${yld !== 1 ? 's' : ''}</span>
    </div>
    ${prepTime ? `<div style="display:flex;align-items:center;gap:5px;padding:5px 12px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:6px;">
      <span style="font-size:12px;">⏱️</span>
      <span style="font-size:11px;font-weight:700;color:#1e40af;">${prepTime} min</span>
    </div>` : ''}
  </div>`;

  const pageBreak = forPrint ? 'page-break-after:always;page-break-inside:avoid;' : '';

  return `
    <div class="kitchen-card" style="background:#ffffff;width:900px;font-family:'Segoe UI',-apple-system,BlinkMacSystemFont,Roboto,sans-serif;border:3px solid #e2e8f0;border-radius:0;box-sizing:border-box;overflow:hidden;${pageBreak}">
      <!-- Header Bar -->
      <div style="background:linear-gradient(135deg,#1e293b 0%,#334155 100%);padding:14px 28px;display:flex;align-items:center;justify-content:space-between;">
        <div style="display:flex;align-items:center;gap:12px;">
          ${brandLogoUrl ? `<img src="${brandLogoUrl}" alt="" style="width:36px;height:36px;object-fit:contain;border-radius:8px;border:2px solid rgba(255,255,255,0.15);" crossorigin="anonymous" />` : ''}
          <div>
            <h1 style="font-size:18px;font-weight:800;color:#ffffff;margin:0;letter-spacing:0.3px;">${itemName}</h1>
            ${brandName ? `<p style="font-size:10px;font-weight:600;color:#94a3b8;margin:2px 0 0;text-transform:uppercase;letter-spacing:1px;">${brandName}</p>` : ''}
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:8px;">
          ${metaBadges.replace(/background:#ecfdf5/g, 'background:rgba(255,255,255,0.1)').replace(/border:1px solid #a7f3d0/g, 'border:1px solid rgba(255,255,255,0.2)').replace(/color:#065f46/g, 'color:#a7f3d0').replace(/background:#eff6ff/g, 'background:rgba(255,255,255,0.1)').replace(/border:1px solid #bfdbfe/g, 'border:1px solid rgba(255,255,255,0.2)').replace(/color:#1e40af/g, 'color:#93c5fd')}
        </div>
      </div>

      <!-- Body: Two Columns -->
      <div style="display:flex;min-height:340px;">
        <!-- LEFT COLUMN: Image + Ingredients -->
        <div style="width:400px;flex-shrink:0;border-right:2px solid #e2e8f0;display:flex;flex-direction:column;">
          ${itemImgUrl ? `<div style="height:160px;overflow:hidden;border-bottom:2px solid #e2e8f0;">
            <img src="${itemImgUrl}" alt="${itemName}" style="width:100%;height:100%;object-fit:cover;" crossorigin="anonymous" />
          </div>` : ''}
          <div style="padding:14px 18px;flex:1;display:flex;flex-direction:column;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:10px;">
              <span style="font-size:13px;">📦</span>
              <span style="font-weight:800;font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#64748b;">Ingredients</span>
              <span style="font-size:10px;font-weight:600;color:#94a3b8;margin-left:auto;">${ingredients.length} items</span>
            </div>
            <table style="width:100%;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:0;overflow:hidden;">
              <thead><tr style="background:#f1f5f9;">
                <th style="padding:4px 10px;text-align:left;font-size:9px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:1px;border-bottom:2px solid #e2e8f0;">Ingredient</th>
                <th style="padding:4px 10px;text-align:right;font-size:9px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:1px;border-bottom:2px solid #e2e8f0;">Quantity</th>
              </tr></thead>
              <tbody>${ingredientRows}</tbody>
            </table>
            ${allergenChips}
          </div>
        </div>

        <!-- RIGHT COLUMN: Prep Steps -->
        <div style="flex:1;padding:18px 22px;display:flex;flex-direction:column;">
          ${steps.length > 0 ? `
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:16px;">
              <span style="font-size:13px;">👨‍🍳</span>
              <span style="font-weight:800;font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#64748b;">Preparation Steps</span>
              <span style="font-size:10px;font-weight:600;color:#94a3b8;margin-left:auto;">${steps.length} steps</span>
            </div>
            <div style="flex:1;">${stepsHtml}</div>
          ` : `<p style="color:#94a3b8;font-size:13px;font-style:italic;margin:auto;">No preparation steps added yet.</p>`}
        </div>
      </div>

      <!-- Footer -->
      <div style="background:#f8fafc;border-top:2px solid #e2e8f0;padding:8px 28px;display:flex;align-items:center;justify-content:space-between;">
        <span style="font-size:8px;font-weight:600;color:#94a3b8;text-transform:uppercase;letter-spacing:1px;">Kitchen Use Only — Confidential</span>
        ${pageNum ? `<span style="font-size:8px;font-weight:700;color:#cbd5e1;">Page ${pageNum}</span>` : ''}
        <span style="font-size:8px;color:#cbd5e1;">ClickIt Kitchen · ${new Date().toLocaleDateString()}</span>
      </div>
    </div>`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   COVER PAGE — Premium book front page
   ═══════════════════════════════════════════════════════════════════════════ */
function buildCoverHtml(brandName?: string, brandLogo?: string, profileName?: string, recipeCount?: number): string {
  const brandLogoUrl = brandLogo ? getMediaUrl(brandLogo) : null;
  return `
    <div style="width:900px;min-height:580px;display:flex;flex-direction:column;font-family:'Segoe UI',-apple-system,BlinkMacSystemFont,Roboto,sans-serif;border:3px solid #1e293b;box-sizing:border-box;overflow:hidden;page-break-after:always;">
      <!-- Top Accent -->
      <div style="height:6px;background:linear-gradient(90deg,#4f46e5,#7c3aed,#ec4899,#f97316);"></div>

      <!-- Main Cover -->
      <div style="flex:1;background:linear-gradient(160deg,#0f172a 0%,#1e293b 40%,#334155 100%);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:48px;text-align:center;position:relative;">
        <!-- Decorative corners -->
        <div style="position:absolute;top:24px;left:24px;width:60px;height:60px;border-top:3px solid rgba(255,255,255,0.08);border-left:3px solid rgba(255,255,255,0.08);"></div>
        <div style="position:absolute;top:24px;right:24px;width:60px;height:60px;border-top:3px solid rgba(255,255,255,0.08);border-right:3px solid rgba(255,255,255,0.08);"></div>
        <div style="position:absolute;bottom:24px;left:24px;width:60px;height:60px;border-bottom:3px solid rgba(255,255,255,0.08);border-left:3px solid rgba(255,255,255,0.08);"></div>
        <div style="position:absolute;bottom:24px;right:24px;width:60px;height:60px;border-bottom:3px solid rgba(255,255,255,0.08);border-right:3px solid rgba(255,255,255,0.08);"></div>

        ${brandLogoUrl ? `<div style="width:110px;height:110px;border-radius:20px;border:3px solid rgba(255,255,255,0.12);overflow:hidden;margin-bottom:28px;background:rgba(255,255,255,0.05);display:flex;align-items:center;justify-content:center;">
          <img src="${brandLogoUrl}" alt="" style="width:90px;height:90px;object-fit:contain;" crossorigin="anonymous" />
        </div>` : ''}

        <div style="width:80px;height:3px;background:linear-gradient(90deg,#4f46e5,#7c3aed);border-radius:2px;margin-bottom:24px;"></div>

        <h1 style="font-size:44px;font-weight:900;color:#ffffff;margin:0;letter-spacing:2px;text-transform:uppercase;">Recipe Book</h1>

        ${brandName ? `<p style="font-size:22px;font-weight:600;margin-top:16px;color:#a78bfa;letter-spacing:0.5px;">${brandName}</p>` : ''}
        ${profileName ? `<p style="font-size:13px;margin-top:8px;color:#64748b;font-weight:500;">${profileName}</p>` : ''}

        <div style="width:120px;height:3px;background:linear-gradient(90deg,#7c3aed,#4f46e5);border-radius:2px;margin-top:32px;"></div>

        ${recipeCount ? `<p style="margin-top:20px;font-size:12px;color:#475569;font-weight:600;letter-spacing:1px;">${recipeCount} RECIPES</p>` : ''}

        <div style="margin-top:auto;padding-top:32px;">
          <p style="font-size:10px;color:#475569;font-weight:500;">Confidential — Kitchen Use Only</p>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div style="background:#0f172a;padding:10px 28px;display:flex;align-items:center;justify-content:between;">
        <span style="font-size:9px;color:#475569;font-weight:600;">Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
        <span style="font-size:9px;color:#475569;font-weight:600;margin-left:auto;">ClickIt Kitchen Platform</span>
      </div>
    </div>`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   TABLE OF CONTENTS PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
function buildTocHtml(items: { name: string; page: number }[], brandName?: string): string {
  const rows = items.map((it, idx) =>
    `<tr style="background:${idx % 2 === 0 ? '#ffffff' : '#f8fafc'};">
      <td style="padding:10px 20px;font-size:13px;color:#1e293b;font-weight:600;border-bottom:1px solid #f1f5f9;">${it.name}</td>
      <td style="padding:10px 20px;font-size:13px;color:#64748b;text-align:right;font-weight:700;border-bottom:1px solid #f1f5f9;">Page ${it.page}</td>
    </tr>`
  ).join('');

  return `
    <div style="width:900px;font-family:'Segoe UI',-apple-system,BlinkMacSystemFont,Roboto,sans-serif;border:3px solid #e2e8f0;box-sizing:border-box;overflow:hidden;page-break-after:always;">
      <div style="background:linear-gradient(135deg,#1e293b,#334155);padding:20px 28px;">
        <h2 style="font-size:22px;font-weight:800;color:#ffffff;margin:0;">Table of Contents</h2>
        ${brandName ? `<p style="font-size:11px;color:#94a3b8;margin:4px 0 0;font-weight:500;">${brandName} Recipe Collection</p>` : ''}
      </div>
      <div style="padding:20px 28px;">
        <table style="width:100%;border-collapse:collapse;border:1px solid #e2e8f0;">
          <thead><tr style="background:#f1f5f9;">
            <th style="padding:10px 20px;text-align:left;font-size:9px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:1px;border-bottom:2px solid #e2e8f0;">Recipe</th>
            <th style="padding:10px 20px;text-align:right;font-size:9px;font-weight:800;color:#64748b;text-transform:uppercase;letter-spacing:1px;border-bottom:2px solid #e2e8f0;">Page</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      <div style="background:#f8fafc;border-top:2px solid #e2e8f0;padding:8px 28px;">
        <span style="font-size:8px;color:#94a3b8;font-weight:600;text-transform:uppercase;letter-spacing:1px;">Total: ${items.length} recipes</span>
      </div>
    </div>`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   STANDALONE RECIPE BOOK GENERATOR
   ═══════════════════════════════════════════════════════════════════════════ */
export async function generateRecipeBook(opts: {
  profileId: string;
  brandName?: string;
  brandLogo?: string;
  profileName?: string;
}): Promise<{ success: boolean; error?: string; count?: number }> {
  const { profileId, brandName, brandLogo, profileName } = opts;
  try {
    const res = await apiFetch<{ data: any[] }>(`/api/menu/recipes/${profileId}`);
    const allRecipes: any[] = res.data || [];
    if (allRecipes.length === 0) return { success: false, error: 'No recipes found in this menu profile.' };

    // Fetch profile structure to resolve item names/images
    const profilesRes = await apiFetch<{ data: any[] }>('/api/menu/profiles');
    const profiles = profilesRes.data || [];
    const thisProfile = profiles.find((p: any) => p.id === profileId);
    const itemMap = new Map<string, any>();
    if (thisProfile?.categories) {
      for (const cat of thisProfile.categories) {
        for (const it of (cat.items || [])) itemMap.set(it.id, it);
      }
    }

    // Filter: only item-level recipes (no option duplicates), must have ingredients
    const itemRecipes = allRecipes.filter(r => {
      if (r.optionId) return false; // skip option-level recipes
      const ings = typeof r.ingredients === 'string' ? JSON.parse(r.ingredients || '[]') : (r.ingredients || []);
      return ings.length > 0;
    });

    if (itemRecipes.length === 0) return { success: false, error: 'No recipes have ingredients yet.' };

    // Build TOC entries + card pages
    const tocEntries: { name: string; page: number }[] = [];
    const pages = itemRecipes.map((r, idx) => {
      const item = itemMap.get(r.menuItemId) as any;
      const ings = typeof r.ingredients === 'string' ? JSON.parse(r.ingredients || '[]') : (r.ingredients || []);
      const allergens = typeof r.allergens === 'string' ? JSON.parse(r.allergens || '[]') : (r.allergens || []);
      const name = item?.nameEN || item?.name || 'Unknown Item';
      const pageNum = idx + 3; // cover=1, toc=2, first recipe=3
      tocEntries.push({ name, page: pageNum });
      return buildCardHtml({
        itemName: name,
        itemImage: item?.image,
        ingredients: ings.map((ri: any) => ({ name: ri.ingredientName, qty: `${ri.usageQty} ${ri.usageUnit}` })),
        steps: (r.prepSteps || '').split('\n').map((s: string) => s.trim()).filter(Boolean),
        allergens,
        yield: r.standardYield || 1,
        prepTime: r.prepTime,
        brandName,
        brandLogo,
        forPrint: true,
        pageNum,
      });
    });

    const cover = buildCoverHtml(brandName, brandLogo, profileName, pages.length);
    const toc = buildTocHtml(tocEntries, brandName);

    const bookHtml = `<html><head>
      <title>Recipe Book${brandName ? ` — ${brandName}` : ''}</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        @media print { .no-print { display: none !important; } }
        * { box-sizing: border-box; }
        body { margin:0; padding:0; background:#e2e8f0; font-family:'Segoe UI',-apple-system,BlinkMacSystemFont,Roboto,sans-serif; }
        .page { display:flex; justify-content:center; padding:24px 0; }
        .toolbar { position:fixed; top:0; left:0; right:0; background:#0f172a; color:white; padding:0 24px; display:flex; align-items:center; height:52px; gap:16px; z-index:99; border-bottom:3px solid #4f46e5; }
        .toolbar .title { font-weight:800; font-size:15px; letter-spacing:0.5px; }
        .toolbar .badge { padding:3px 10px; background:rgba(255,255,255,0.08); border:1px solid rgba(255,255,255,0.12); border-radius:6px; font-size:11px; font-weight:600; color:#94a3b8; }
        .toolbar .spacer { flex:1; }
        .toolbar button { padding:8px 20px; border-radius:8px; border:none; font-weight:700; font-size:12px; cursor:pointer; display:flex; align-items:center; gap:6px; transition:all 0.15s; }
        .btn-pdf { background:linear-gradient(135deg,#4f46e5,#7c3aed); color:white; }
        .btn-pdf:hover { opacity:0.9; }
        .btn-print { background:rgba(255,255,255,0.08); color:#e2e8f0; border:1px solid rgba(255,255,255,0.15) !important; }
        .btn-print:hover { background:rgba(255,255,255,0.15); }
        .btn-close { background:rgba(255,255,255,0.05); color:#94a3b8; }
        .btn-close:hover { background:rgba(255,255,255,0.1); color:white; }
        .spacer-bar { height:64px; }
      </style>
    </head><body>
      <div class="toolbar no-print">
        <span class="title">📕 Recipe Book</span>
        <span class="badge">${pages.length} recipe${pages.length !== 1 ? 's' : ''}</span>
        ${brandName ? `<span class="badge">${brandName}</span>` : ''}
        <div class="spacer"></div>
        <button class="btn-pdf" onclick="window.print()">📄 Save as PDF</button>
        <button class="btn-print" onclick="window.print()">🖨️ Print</button>
        <button class="btn-close" onclick="window.close()">✕ Close</button>
      </div>
      <div class="spacer-bar no-print"></div>
      <div class="page">${cover}</div>
      <div class="page">${toc}</div>
      ${pages.map(p => `<div class="page">${p}</div>`).join('')}
    </body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(bookHtml); w.document.close(); }
    return { success: true, count: pages.length };
  } catch (err) {
    console.error(err);
    return { success: false, error: 'Could not generate recipe book.' };
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   SINGLE CARD PREVIEW PANEL
   ═══════════════════════════════════════════════════════════════════════════ */
export function KitchenCardPreview({
  open, onClose, recipe, itemName, itemImage, profileId, brandName, brandLogo,
}: KitchenCardPreviewProps) {
  const toast = useToast();
  const cardRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);

  const card = formatKitchenCard(recipe, itemName, itemImage);

  const cardHtml = buildCardHtml({
    ...card,
    yield: card.yield,
    brandName,
    brandLogo,
  });

  const handleDownloadPng = useCallback(async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const el = cardRef.current.querySelector('.kitchen-card') as HTMLElement;
      if (!el) throw new Error('Card element not found');
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
      canvas.toBlob(blob => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `KitchenCard_${itemName.replace(/[^a-zA-Z0-9]/g, '_')}.png`;
        a.click();
        URL.revokeObjectURL(url);
      }, 'image/png');
      toast.success('Exported', 'Kitchen card PNG downloaded.');
    } catch {
      toast.error('Export Failed', 'Could not export kitchen card.');
    }
    setDownloading(false);
  }, [itemName, toast]);

  const handlePrint = useCallback(() => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>Kitchen Card - ${itemName}</title><style>@page{size:landscape;margin:12mm;}body{margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;}</style></head><body>${cardHtml}</body></html>`);
    w.document.close();
    w.focus();
    w.print();
  }, [cardHtml, itemName]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[60] backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 w-[980px] max-w-full bg-slate-50 shadow-2xl z-[61] flex flex-col animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between px-5 py-3 bg-white border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-lg">🍳</span>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Kitchen Recipe Card</h2>
              <p className="text-[11px] text-slate-400 font-medium">{itemName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-[11px] font-bold hover:bg-slate-50 transition-colors">
              <Printer size={12} /> Print
            </button>
            <button onClick={handleDownloadPng} disabled={downloading}
              className="flex items-center gap-1.5 px-3 py-2 bg-teal-600 text-white rounded-lg text-[11px] font-bold hover:bg-teal-700 disabled:opacity-50 transition-colors shadow-sm">
              {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              Download PNG
            </button>
            <button onClick={onClose}
              className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-6 flex justify-center">
          <div ref={cardRef} dangerouslySetInnerHTML={{ __html: cardHtml }} />
        </div>
      </div>
    </>
  );
}
